// Nomad-mon Go! — SC8 card-power education. DOM-only (reading text never in
// canvas): (a) the PWR popover — tap any PWR badge for a glass breakdown of
// the number; (b) the guide sheet — static "How to play" and "Type chart" pages.
// The type-wheel and keyword builders live here for battle.js's cheat sheet.
import { TYPES, TYPE_ORDER, KEYWORDS, typeGlyph, pwr } from './types.js';
import * as audio from './audio.js';

const RCOL = { common: '#b8c4cc', rare: '#4fd8ff', epic: '#b98aff', legendary: '#f0c463', mythic: '#ff9ae8', eternal: '#ffe9a8' };
const COACH_KEY = 'nomadmon-pwr-coach';
const GUIDE_KEY = 'nomadmon-guide-seen';

// ---------------------------------------------------------------- type wheel
// Shared builder: SVG arc arrows + glyph nodes, positioned in % of container.
// Containers style via the #typeWheel / .twheel rules in index.html.
export function typeWheelHTML() {
  const R = 50 - 11;
  let arrows = '<svg class="ring" viewBox="0 0 100 100" fill="none">';
  for (let i = 0; i < 6; i++) {
    const t = TYPE_ORDER[i];
    const a0 = (i / 6) * Math.PI * 2 - Math.PI / 2;
    const a1 = ((i + 1) / 6) * Math.PI * 2 - Math.PI / 2;
    const trim = 0.42;
    const s0 = a0 + trim, s1 = a1 - trim;
    const p = (a, r = R) => `${50 + Math.cos(a) * r},${50 + Math.sin(a) * r}`;
    const mid = (s0 + s1) / 2;
    arrows += `<path d="M${p(s0)} Q${p(mid, R + 7)} ${p(s1)}" stroke="${TYPES[t].color}" ` +
      `stroke-width="1.6" opacity=".85"/>`;
    const hx = 50 + Math.cos(s1) * R, hy = 50 + Math.sin(s1) * R;
    const dir = s1 + Math.PI / 2 + 0.18;
    arrows += `<path d="M${hx},${hy} l${Math.cos(dir + 2.6) * 3.4},${Math.sin(dir + 2.6) * 3.4} ` +
      `M${hx},${hy} l${Math.cos(dir - 2.6) * 3.4},${Math.sin(dir - 2.6) * 3.4}" ` +
      `stroke="${TYPES[t].color}" stroke-width="1.6" stroke-linecap="round"/>`;
  }
  arrows += '</svg>';
  let nodes = '';
  for (let i = 0; i < 6; i++) {
    const t = TYPE_ORDER[i];
    const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
    const x = 50 + Math.cos(a) * R, y = 50 + Math.sin(a) * R;
    nodes += `<div class="tw-node" style="left:${x}%;top:${y}%;--tc:${TYPES[t].color}">` +
      `<span class="g">${typeGlyph(t, 26)}</span><span class="l">${TYPES[t].label}</span></div>`;
  }
  return arrows + nodes;
}

export function keywordListHTML() {
  return Object.entries(KEYWORDS)
    .map(([k, v]) => `<div class="ts-kw"><b>${k}</b> — ${v}</div>`).join('');
}

// ---------------------------------------------------------------- PWR popover
let popEl = null;
function ensurePop() {
  if (popEl) return popEl;
  popEl = document.createElement('div');
  popEl.id = 'pwrPop';
  document.body.appendChild(popEl);
  popEl.addEventListener('click', (e) => { e.stopPropagation(); closePwrPop(); });
  return popEl;
}
export function closePwrPop() { if (popEl) popEl.classList.remove('open'); }

// Breakdown mirrors pwrRaw exactly: hp*0.35 + basic + heavy*0.5 + special*0.7
// + spd*2, normalized into display points that sum to the badge.
export function openPwrPop(d, anchor, shownPwr = null) {
  const el = ensurePop();
  const P = shownPwr == null ? pwr(d) : shownPwr;
  const isChar = (d.hp > 0) && Array.isArray(d.moves) && d.moves.length;
  let rows = '';
  if (isChar) {
    const basic = d.moves[0].dmg || 0;
    const rest = d.moves.slice(1).map(m => m.dmg || 0);
    const special = rest.length ? Math.max(...rest) : 0;
    const parts = [
      ['HP', d.hp, d.hp * 0.35],
      ['MOVES', `${basic} / ${special}`, basic + (basic * 1.6) * 0.5 + special * 0.7],
      ['SPEED', d.spd || 0, (d.spd || 0) * 2],
    ];
    const total = parts.reduce((a, p) => a + p[2], 0);
    // display points sum to the badge: the formula's fixed bias is folded in
    // proportionally so HP + MOVES + SPEED = PWR on screen
    rows = parts.map(([k, v, raw]) =>
      `<div class="pp-row"><span class="k">${k}</span><span class="v">${v}</span>` +
      `<span class="bar"><i style="width:${Math.round(raw / total * 100)}%"></i></span>` +
      `<span class="pts">+${Math.max(1, Math.round(raw / total * P))}</span></div>`).join('');
  } else {
    rows = `<div class="pp-row"><span class="k">SUPPORT</span>` +
      `<span class="v">rated by rarity band</span></div>`;
  }
  const rc = RCOL[d.rarity] || '#b8c4cc';
  el.style.setProperty('--rc', rc);
  el.innerHTML = `
    <div class="pp-head"><b>${P}</b><span>PWR</span></div>
    <div class="pp-sub">the whole kit, one number — higher = deadlier</div>
    ${rows}
    <div class="pp-tier">
      <span class="lo">1</span>
      <span class="track">${['common', 'rare', 'epic', 'legendary', 'mythic', 'eternal']
        .map(r => `<i class="seg" style="background:${RCOL[r]}"></i>`).join('')}
        <i class="mark" style="left:${((P - 1) / 98 * 100).toFixed(1)}%"></i></span>
      <span class="hi">99</span>
    </div>
    <div class="pp-tsub">${d.rarity.toUpperCase()} tier · tap to close</div>`;
  // anchor near the badge, clamped to the viewport
  const r = anchor.getBoundingClientRect();
  el.classList.add('open');
  const w = Math.min(280, innerWidth - 20);
  el.style.width = w + 'px';
  let x = Math.min(Math.max(10, r.left + r.width / 2 - w / 2), innerWidth - w - 10);
  el.style.left = x + 'px';
  const h = el.offsetHeight || 200;
  el.style.top = (r.top > h + 24 ? r.top - h - 10 : Math.min(r.bottom + 10, innerHeight - h - 10)) + 'px';
}

// First-time coach mark: pulse the given badge once, ever (per browser).
export function coachPwr(badge) {
  if (localStorage.getItem(COACH_KEY)) return;
  localStorage.setItem(COACH_KEY, '1');
  badge.classList.add('pwrcoach');
  setTimeout(() => badge.classList.remove('pwrcoach'), 3600);
}

// Wire a badge element to the popover for a card datum.
export function bindPwrBadge(elm, d, shownPwr = null) {
  if (!elm) return;
  elm.style.cursor = 'pointer';
  elm.dataset.pwrBadge = '1';           // nav: a badge toggles its popover, it is not an "outside" tap
  elm.onclick = (e) => { e.stopPropagation(); openPwrPop(d, elm, shownPwr); };
}

// ---------------------------------------------------------------- guide sheet
// Two static pages in one sheet: HOW TO PLAY (Combat V3 rules, docs/COMBAT_V3.md)
// and the TYPE CHART. Read-only, large type, nothing to interact with.
const PLAY_HTML = `
  <h4>How to play</h4>
  <p class="gd-lede">Knock out three of your opponent's fighters to win.</p>
  <section class="gd-sec"><h5>Your squad</h5>
    <p>Pick three fighters; the slot machine deals the other two (online you pick all five).
    The five must fit <b>15 stars</b>: common 1, rare 2, epic 3, legendary 4, mythic and eternal 5.
    Two supports are added for you.</p></section>
  <section class="gd-sec"><h5>Attack by drawing</h5>
    <p>Pick a move, then trace its sigil. The cleaner the trace, the harder it hits.</p>
    <dl class="gd-moves">
      <div><dt>Slash</dt><dd>Fast and safe. Hits first, and even a scribble does over half damage.</dd></div>
      <div><dt>Rune</dt><dd>Stronger. Smashes straight through a guard.</dd></div>
      <div><dt>Glyph</dt><dd>The biggest hit, but it lands last. A sloppy trace fizzles to almost nothing.</dd></div>
    </dl></section>
  <section class="gd-sec"><h5>Guard</h5>
    <p>Halves a slash. Stops a glyph cold and hits back. A rune breaks it.
    A slash that lands first also weakens an enemy glyph.</p></section>
  <section class="gd-sec"><h5>Tag and support</h5>
    <p><b>Tag</b> brings in a fighter from your bench. You skip your attack,
    and the newcomer takes half damage as it lands.</p>
    <p><b>Support</b> plays a spell, which hits right away, or sets a trap,
    which springs on the next enemy attack. Each one works once.</p></section>
  <section class="gd-sec"><h5>Surge and Ult</h5>
    <p>Clean traces, hits you take and parries fill your <b>surge</b> meter.
    When it's full, your <b>Ult</b> is ready: your hardest hit, and it can't be parried.</p></section>
  <section class="gd-sec"><h5>Holo and Awaken</h5>
    <p>Legendary, mythic and eternal cards are born <b>Holo</b>. Any other card
    turns Holo at level 5, after about a dozen wins.</p>
    <p>Land a Holo card's Ult with a <b>Perfect</b> trace and it <b>Awakens</b>
    for the rest of the battle: 15% more damage, and its hits ignore guard.</p></section>`;

function typesHTML() {
  const rows = TYPE_ORDER.map((t) => {
    const T = TYPES[t], B = TYPES[T.beats], W = TYPES[T.weakTo];
    return `<li style="--tc:${T.color}"><span class="gd-t">${typeGlyph(t, 30)}<b>${T.label}</b></span>` +
      `<span class="gd-rel"><span>Beats <b style="color:${B.color}">${B.label}</b></span>` +
      `<span>Weak to <b style="color:${W.color}">${W.label}</b></span></span></li>`;
  }).join('');
  return `
  <h4>Type chart</h4>
  <p class="gd-lede">Hit a type you beat for <b>double damage</b>.
  Each type also takes 2 less damage from the type it beats.</p>
  <ul class="gd-types">${rows}</ul>`;
}

let guideEl = null;
function ensureGuide() {
  if (guideEl) return guideEl;
  guideEl = document.createElement('div');
  guideEl.id = 'guideSheet';
  guideEl.setAttribute('role', 'dialog');
  guideEl.setAttribute('aria-modal', 'true');
  guideEl.innerHTML = `
    <button id="guideClose" class="nav-x" type="button" aria-label="Close">&#10005;</button>
    <div class="gd-body"></div>`;
  document.body.appendChild(guideEl);
  guideEl.querySelector('#guideClose').addEventListener('click', () => closeGuide());
  return guideEl;
}
/** kind: 'play' (How to play, the default) or 'types' (Type chart). */
export function openGuide(kind = 'play') {
  const el = ensureGuide();
  el.dataset.kind = kind === 'types' ? 'types' : 'play';
  el.querySelector('.gd-body').innerHTML = kind === 'types' ? typesHTML() : PLAY_HTML;
  el.scrollTop = 0;
  el.classList.add('open');
  document.body.classList.add('guideopen');
  if (!localStorage.getItem(GUIDE_KEY)) {
    localStorage.setItem(GUIDE_KEY, '1');
    audio.vo('guide-intro');               // Shao walks the new scholar in
  } else if (!sessionStorage.getItem('nomadmon-guide2') && Math.random() < 0.4) {
    // SC14: returning scholars occasionally get the revisit line (session-once)
    sessionStorage.setItem('nomadmon-guide2', '1');
    audio.vo('screen-guide2');
  }
}
export function closeGuide() {
  if (guideEl) guideEl.classList.remove('open');
  document.body.classList.remove('guideopen');
}
